import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Megaphone, MessageSquare, Mail, Smartphone, Pencil, Check, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { SendStatusDialog } from "@/components/events/CommFlow";
import { TestSendMenu } from "@/components/comm/TestSendBlock";

// Popup voor Wouter/Charissa: eventberichten die klaarstaan ter goedkeuring.
export default function CommApprovalPopup() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [testBusy, setTestBusy] = useState(null);
  const [statusJob, setStatusJob] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [editing, setEditing] = useState(null); // { id, message, subject }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/events/comm-approvals/pending")
      .then((r) => {
        const list = r.data?.items || [];
        setItems((cur) => {
          const curIds = new Set(cur.map((i) => i.id));
          if (list.some((i) => !curIds.has(i.id))) setDismissed(false);
          return list;
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const decide = async (p, send) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/events/comm-approvals/${p.id}/decide`, { send });
      setItems((cur) => cur.filter((x) => x.id !== p.id));
      if (send && data.job_id) setStatusJob({ jobId: data.job_id, title: p.title });
      else toast.success(send ? "Wordt verstuurd" : `"${p.title}" wordt niet verstuurd`);
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); load(); }
    finally { setBusy(null); }
  };

  const sendTest = async (p, toEmail) => {
    setTestBusy(p.id);
    try {
      const { data } = await api.post(`/events/comm-approvals/${p.id}/test`, { to_email: toEmail });
      if (data.ok) toast.success(data.message);
      else toast.warning(data.message, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Test versturen mislukt"); }
    finally { setTestBusy(null); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/events/comm-approvals/${editing.id}`, { message: editing.message, subject: editing.subject });
      toast.success("Bericht bijgewerkt — beoordeel nu wat de actie wordt");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const open = items.length > 0 && !dismissed;
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && setDismissed(true)}>
        <DialogContent className="sm:max-w-xl" data-testid="comm-approval-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-600" /> Eventbericht{items.length > 1 ? "en" : ""} klaar voor verzending
            </DialogTitle>
            <DialogDescription>Beoordeel per bericht of het nu verstuurd mag worden naar de aangemelde deelnemers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {items.map((p) => (
              <div key={p.id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`comm-approval-${p.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {p.channel === "whatsapp"
                    ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><MessageSquare className="h-3 w-3" /> WhatsApp</span>
                    : <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700"><Mail className="h-3 w-3" /> E-mail</span>}
                  <span className="text-sm font-semibold text-slate-800">{p.title}</span>
                  <span className="text-xs text-slate-400">· {p.event_name} · {p.recipients_count} deelnemers</span>
                </div>
                {editing?.id === p.id ? (
                  <div className="space-y-2" data-testid={`comm-popup-editor-${p.id}`}>
                    {p.channel === "email" && (
                      <Input value={editing.subject || ""} placeholder="Onderwerp"
                        onChange={(e) => setEditing((c) => ({ ...c, subject: e.target.value }))}
                        className="h-8 text-xs" data-testid={`comm-popup-edit-subject-${p.id}`} />
                    )}
                    <Textarea value={editing.message} rows={8}
                      onChange={(e) => setEditing((c) => ({ ...c, message: e.target.value }))}
                      className="text-xs" data-testid={`comm-popup-edit-message-${p.id}`} />
                    <p className="text-[10px] text-slate-400">
                      Tags zoals {"{voornaam}"}, {"{eventnaam}"}, {"{eventdatum}"}, {"{eventstarttijd}"} en {"{eventadres}"} worden bij verzending automatisch ingevuld.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={saving}
                        onClick={() => setEditing(null)} data-testid={`comm-popup-edit-cancel-${p.id}`}>
                        <X className="h-3.5 w-3.5" /> Annuleren
                      </Button>
                      <Button size="sm" className="h-7 text-xs gap-1 bg-slate-800 hover:bg-slate-900" disabled={saving || !editing.message?.trim()}
                        onClick={saveEdit} data-testid={`comm-popup-edit-save-${p.id}`}>
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Opslaan
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-slate-100 bg-slate-50/60 p-2 max-h-52 overflow-y-auto" data-testid={`comm-popup-message-${p.id}`}>
                    {p.channel === "email" && p.subject && <p className="text-xs font-semibold text-slate-600 mb-1">Onderwerp: {p.subject}</p>}
                    <p className="text-xs text-slate-600 whitespace-pre-wrap">{p.message}</p>
                  </div>
                )}
                {editing?.id !== p.id && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <TestSendMenu testId={`comm-popup-test-menu-${p.id}`} onPick={(email) => sendTest(p, email)} trigger={
                    <Button size="sm" variant="outline" disabled={testBusy === p.id || busy === p.id}
                      className="h-7 text-xs gap-1 text-slate-600" data-testid={`comm-popup-test-${p.id}`}>
                      {testBusy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : p.channel === "whatsapp" ? <Smartphone className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />} Stuur een test
                    </Button>} />
                  <Button size="sm" variant="outline" disabled={busy === p.id} data-testid={`comm-popup-edit-${p.id}`}
                    onClick={() => setEditing({ id: p.id, message: p.message_raw || p.message || "", subject: p.subject_raw || p.subject || "" })}
                    className="h-7 text-xs gap-1 text-slate-600 mr-auto">
                    <Pencil className="h-3.5 w-3.5" /> Bewerken
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => decide(p, false)}
                    className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 ml-auto" data-testid={`comm-popup-skip-${p.id}`}>
                    ✖ Niet verzenden
                  </Button>
                  <Button size="sm" disabled={busy === p.id} onClick={() => decide(p, true)}
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid={`comm-popup-send-${p.id}`}>
                    {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "✅"} Verzenden
                  </Button>
                </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setDismissed(true)}
              data-testid="comm-popup-later">
              Later beoordelen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SendStatusDialog jobId={statusJob?.jobId} title={statusJob?.title} open={!!statusJob} onClose={() => setStatusJob(null)} />
    </>
  );
}
